import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../services/api';

interface PartnerApiKey {
  id: string;
  partner: string;
  name: string;
  keyPrefix: string;
  allowedTenantIds: string[];
  scopes: string[];
  isActive: boolean;
  expiresAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
}

interface OutboundWebhook {
  id: string;
  partnerApiKeyId: string;
  tenantId: string | null;
  url: string;
  event: string;
  isActive: boolean;
  createdAt: string;
}

interface WebhookDelivery {
  id: string;
  event: string;
  status: string;
  responseStatus: number | null;
  attemptNumber: number;
  errorMessage: string | null;
  createdAt: string;
}

const PARTNER_TYPES = ['CDK', 'REYNOLDS', 'VONAGE', 'CUSTOM'];
const ALL_SCOPES = [
  'partner:videos:read',
  'partner:scores:read',
  'partner:compliance:read',
  'partner:benchmarks:read',
  'partner:upload',
];
const ALL_EVENTS = [
  'video.scored',
  'video.compliance_flagged',
  'video.hard_blocked',
  'coaching.note_created',
];

const PartnerIntegrationsPanel: React.FC = () => {
  const [keys, setKeys] = useState<PartnerApiKey[]>([]);
  const [webhooks, setWebhooks] = useState<OutboundWebhook[]>([]);
  const [deliveries, setDeliveries] = useState<Record<string, WebhookDelivery[]>>({});
  const [loading, setLoading] = useState(true);

  // Create key form
  const [showCreate, setShowCreate] = useState(false);
  const [createPartner, setCreatePartner] = useState('CDK');
  const [createName, setCreateName] = useState('');
  const [createScopes, setCreateScopes] = useState<string[]>([]);
  const [createTenants, setCreateTenants] = useState('');
  const [creating, setCreating] = useState(false);
  const [newlyIssuedKey, setNewlyIssuedKey] = useState<string | null>(null);

  // Create webhook form
  const [whPartnerKeyId, setWhPartnerKeyId] = useState('');
  const [whUrl, setWhUrl] = useState('');
  const [whEvent, setWhEvent] = useState<string>('video.scored');
  const [whTenant, setWhTenant] = useState('');
  const [creatingWebhook, setCreatingWebhook] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [keysRes, whRes] = await Promise.all([
        api.get('/admin/partner-keys'),
        api.get('/admin/partner-webhooks'),
      ]);
      setKeys(keysRes.data.data ?? []);
      setWebhooks(whRes.data.data ?? []);
    } catch {
      toast.error('Failed to load partner integrations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreateKey = async () => {
    if (!createName.trim() || createScopes.length === 0) {
      toast.error('Name and at least one scope are required.');
      return;
    }
    setCreating(true);
    try {
      const allowedTenantIds = createTenants
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const res = await api.post('/admin/partner-keys', {
        partner: createPartner,
        name: createName.trim(),
        scopes: createScopes,
        allowedTenantIds,
      });
      const { rawKey } = res.data.data as { rawKey: string };
      setNewlyIssuedKey(rawKey);
      setCreateName('');
      setCreateScopes([]);
      setCreateTenants('');
      setShowCreate(false);
      toast.success('API key issued. Copy it now — it will not be shown again.');
      await load();
    } catch {
      toast.error('Failed to issue key.');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id: string) => {
    if (!window.confirm('Revoke this key? All partner requests using it will fail.')) return;
    try {
      await api.post(`/admin/partner-keys/${id}/revoke`);
      toast.success('Key revoked.');
      await load();
    } catch {
      toast.error('Failed to revoke.');
    }
  };

  const handleRotate = async (id: string) => {
    if (!window.confirm('Rotate this key? The existing key will stop working immediately.')) return;
    try {
      const res = await api.post(`/admin/partner-keys/${id}/rotate`);
      setNewlyIssuedKey((res.data.data as { rawKey: string }).rawKey);
      toast.success('Key rotated. Copy the new value now.');
      await load();
    } catch {
      toast.error('Failed to rotate.');
    }
  };

  const handleCreateWebhook = async () => {
    if (!whPartnerKeyId || !whUrl.trim()) {
      toast.error('Partner key and URL are required.');
      return;
    }
    setCreatingWebhook(true);
    try {
      await api.post('/admin/partner-webhooks', {
        partnerApiKeyId: whPartnerKeyId,
        url: whUrl.trim(),
        event: whEvent,
        tenantId: whTenant.trim() || null,
      });
      toast.success('Webhook subscribed.');
      setWhUrl('');
      setWhTenant('');
      await load();
    } catch {
      toast.error('Failed to create webhook.');
    } finally {
      setCreatingWebhook(false);
    }
  };

  const handleLoadDeliveries = async (webhookId: string) => {
    if (deliveries[webhookId]) {
      // toggle off
      const copy = { ...deliveries };
      delete copy[webhookId];
      setDeliveries(copy);
      return;
    }
    try {
      const res = await api.get(`/admin/partner-webhooks/${webhookId}/deliveries?limit=25`);
      setDeliveries((prev) => ({ ...prev, [webhookId]: res.data.data ?? [] }));
    } catch {
      toast.error('Failed to load delivery history.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-3 border-primary border-t-transparent animate-spin rounded-full" />
      </div>
    );
  }

  return (
    <section className="space-y-8">
      <div className="flex items-center justify-between gap-4 border-b border-slate-100 dark:border-white/5 pb-5">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-950/20 flex items-center justify-center text-purple-500">
            <span className="material-symbols-outlined text-[24px]">hub</span>
          </div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">Partner Integrations</h2>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate((v) => !v)}
          className="h-12 px-8 rounded-2xl bg-primary text-white font-bold text-xs uppercase tracking-widest shadow-lg shadow-primary/20 hover:bg-primary/90 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center gap-2"
        >
          <span className="material-symbols-outlined text-sm">add</span>
          {showCreate ? 'Cancel' : 'Issue API Key'}
        </button>
      </div>

      {newlyIssuedKey && (
        <div className="p-5 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40 rounded-2xl">
          <p className="text-[10px] font-black uppercase tracking-widest text-amber-600 mb-2">
            Copy this key now — it cannot be retrieved later
          </p>
          <div className="flex items-center gap-3">
            <code className="flex-1 p-3 rounded-lg bg-white dark:bg-slate-900 border border-amber-200 text-xs font-mono break-all">
              {newlyIssuedKey}
            </code>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(newlyIssuedKey);
                toast.success('Copied to clipboard');
              }}
              className="h-10 px-4 rounded-lg bg-amber-500 text-white text-[10px] font-black uppercase tracking-widest"
            >
              Copy
            </button>
            <button
              type="button"
              onClick={() => setNewlyIssuedKey(null)}
              className="h-10 px-4 rounded-lg text-slate-400 text-[10px] font-black uppercase tracking-widest hover:text-slate-700"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {showCreate && (
        <div className="bg-white dark:bg-slate-900 border border-primary/20 rounded-2xl p-6 shadow-sm space-y-4">
          <h3 className="font-bold text-slate-900 dark:text-white">Issue Partner API Key</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1.5">
                Partner *
              </label>
              <select
                value={createPartner}
                onChange={(e) => setCreatePartner(e.target.value)}
                className="h-10 w-full px-3 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 text-sm font-medium"
              >
                {PARTNER_TYPES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1.5">
                Name *
              </label>
              <input
                type="text"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="e.g. CDK East US Production"
                className="h-10 w-full px-3 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 text-sm font-medium"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1.5">
              Scopes *
            </label>
            <div className="flex flex-wrap gap-2">
              {ALL_SCOPES.map((scope) => {
                const checked = createScopes.includes(scope);
                return (
                  <button
                    key={scope}
                    type="button"
                    onClick={() =>
                      setCreateScopes((prev) =>
                        checked ? prev.filter((s) => s !== scope) : [...prev, scope],
                      )
                    }
                    className={`px-3 h-8 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${
                      checked
                        ? 'bg-primary text-white'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200'
                    }`}
                  >
                    {scope}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1.5">
              Allowed Account IDs (comma-separated — leave blank for all)
            </label>
            <input
              type="text"
              value={createTenants}
              onChange={(e) => setCreateTenants(e.target.value)}
              placeholder="uuid-1, uuid-2, ..."
              className="h-10 w-full px-3 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 text-sm font-mono"
            />
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleCreateKey}
              disabled={creating || !createName.trim() || createScopes.length === 0}
              className="h-10 px-6 rounded-xl bg-primary text-white font-black text-xs uppercase tracking-widest disabled:opacity-40"
            >
              {creating ? 'Issuing...' : 'Issue Key'}
            </button>
          </div>
        </div>
      )}

      {/* Key list */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
        {keys.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-400">No partner API keys issued yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/20">
                {['Partner', 'Name', 'Key Prefix', 'Scopes', 'Accounts', 'Status', 'Last Used', 'Actions'].map((h) => (
                  <th
                    key={h}
                    className="px-3 py-3 text-left text-[10px] font-black uppercase tracking-widest text-slate-400"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id} className="border-b border-slate-50 dark:border-slate-800/50">
                  <td className="px-3 py-3 font-bold text-xs">{k.partner}</td>
                  <td className="px-3 py-3 text-xs">{k.name}</td>
                  <td className="px-3 py-3 font-mono text-[11px] text-slate-500">{k.keyPrefix}…</td>
                  <td className="px-3 py-3 text-[10px] text-slate-500">{k.scopes.length} scopes</td>
                  <td className="px-3 py-3 text-[10px] text-slate-500">
                    {k.allowedTenantIds.length === 0 ? 'All' : `${k.allowedTenantIds.length} accounts`}
                  </td>
                  <td className="px-3 py-3">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest ${
                        k.isActive
                          ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30'
                          : 'bg-slate-100 text-slate-500 dark:bg-slate-800'
                      }`}
                    >
                      {k.isActive ? 'Active' : 'Revoked'}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-[10px] text-slate-500">
                    {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : 'Never'}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleRotate(k.id)}
                        className="h-7 px-2 rounded-lg text-[9px] font-black uppercase tracking-widest bg-amber-50 text-amber-600 hover:bg-amber-100 dark:bg-amber-950/30"
                      >
                        Rotate
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRevoke(k.id)}
                        className="h-7 px-2 rounded-lg text-[9px] font-black uppercase tracking-widest bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-950/30"
                      >
                        Revoke
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Webhooks */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-4">
        <h3 className="font-bold text-slate-900 dark:text-white">Outbound Webhooks</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <select
            value={whPartnerKeyId}
            onChange={(e) => setWhPartnerKeyId(e.target.value)}
            className="h-10 px-3 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 text-sm"
          >
            <option value="">Select partner key...</option>
            {keys.filter((k) => k.isActive).map((k) => (
              <option key={k.id} value={k.id}>
                {k.partner} — {k.name}
              </option>
            ))}
          </select>
          <input
            type="url"
            value={whUrl}
            onChange={(e) => setWhUrl(e.target.value)}
            placeholder="https://partner.example.com/hook"
            className="h-10 px-3 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 text-sm"
          />
          <select
            value={whEvent}
            onChange={(e) => setWhEvent(e.target.value)}
            className="h-10 px-3 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 text-sm"
          >
            {ALL_EVENTS.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={whTenant}
            onChange={(e) => setWhTenant(e.target.value)}
            placeholder="Account ID (blank=all)"
            className="h-10 px-3 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 text-sm font-mono"
          />
        </div>
        <button
          type="button"
          onClick={handleCreateWebhook}
          disabled={creatingWebhook || !whPartnerKeyId || !whUrl}
          className="h-10 px-6 rounded-xl bg-primary text-white font-black text-xs uppercase tracking-widest disabled:opacity-40"
        >
          Subscribe
        </button>
        <div className="space-y-2">
          {webhooks.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">No webhook subscriptions yet.</p>
          ) : (
            webhooks.map((w) => (
              <div key={w.id} className="border border-slate-100 dark:border-slate-800 rounded-xl p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-slate-900 dark:text-white truncate">{w.url}</div>
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                      {w.event} · {w.tenantId ? 'Account-scoped' : 'All accounts'}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleLoadDeliveries(w.id)}
                    className="h-8 px-3 rounded-lg text-[10px] font-black uppercase tracking-widest bg-slate-100 dark:bg-slate-800 hover:bg-slate-200"
                  >
                    {deliveries[w.id] ? 'Hide' : 'Deliveries'}
                  </button>
                </div>
                {deliveries[w.id] && (
                  <div className="mt-3 space-y-1 text-[11px]">
                    {deliveries[w.id].length === 0 ? (
                      <p className="text-slate-400">No deliveries yet.</p>
                    ) : (
                      deliveries[w.id].map((d) => (
                        <div
                          key={d.id}
                          className="flex items-center gap-3 p-2 bg-slate-50 dark:bg-slate-950 rounded-md"
                        >
                          <span
                            className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest ${
                              d.status === 'SUCCEEDED'
                                ? 'bg-emerald-100 text-emerald-600'
                                : d.status === 'DEAD_LETTER'
                                  ? 'bg-red-100 text-red-600'
                                  : 'bg-amber-100 text-amber-600'
                            }`}
                          >
                            {d.status}
                          </span>
                          <span className="text-slate-500">
                            #{d.attemptNumber} · HTTP {d.responseStatus ?? '—'}
                          </span>
                          <span className="text-slate-400 ml-auto">{new Date(d.createdAt).toLocaleString()}</span>
                          {d.errorMessage && (
                            <span className="text-red-500 truncate max-w-xs">{d.errorMessage}</span>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
};

export default PartnerIntegrationsPanel;
